import { deriveDuration } from '~libs/dayjs';
import {
  deriveAccountsDefinition,
  deriveAccountSettings,
  deriveClientAddress,
  deriveClientLink,
  deriveServiceTypeDescription,
  deriveServiceTypeSelectorParams,
} from '~appointments/lib/derive';
import { initialise, initialiseUserOption } from '~appointments/lib/initialise';
import { onChangePackageHandler, onChangeServiceType, onChangeTime } from '~appointments/lib/onchange';
import {
  showClientDescription,
  showClientHoldCheck,
  showEquipment,
  showLocations,
  showMedications,
  showPaymentAmount,
  showPaymentCollected,
  showPhotos,
  showServiceBranches,
  showTransport,
  showUserAvailability,
} from '~appointments/lib/show';
import {
  validateCareWorker,
  validatePackageServiceTypeCompatibility,
  validateStartEndTimes,
  validateTasks,
  validateUserAvailability,
  validateUserQualifications,
} from '~appointments/lib/validate';

import { FieldProps } from '~components/form-fields';
import LabelDuration from '~components/LabelDuration';
import {
  FormInputAccountSettings,
  FormInputCareWorkers,
  FormInputClaimLink,
  FormInputClientHoldCheck,
  FormInputEquipments,
  FormInputImage,
  FormInputLocations,
  FormInputNDISRates,
  FormInputPackageSelector,
  FormInputQualifications,
  FormInputStartEndTimes,
  FormInputTasks,
  FormInputWorkerAppointments,
  FormInputWorkerAvailability,
  FormInputWorkerLeaveAvailability,
  FormInputWorkerQualificationCheck,
  FormInputWorkerRosterNotes,
} from '~appointments/components';

import { getAll as getLocations } from '~appointments/actions/locations';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getServiceBranches } from '~lookups/actions/serviceBranches';

import { selectCareQualificationLevels } from '~appointments/selectors/careQualificationLevels';
import { selectLocations } from '~appointments/selectors/locations';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectEquipments } from '~lookups/selectors/equipment';
import { selectMedications } from '~lookups/selectors/medications';
import { selectPaymentTypes } from '~lookups/selectors/paymentTypes';
import { selectServiceBranches } from '~lookups/selectors/serviceBranches';
import { selectTaskTypes } from '~lookups/selectors/taskTypes';

const commonFields: FieldProps = {
  id: {
    type: 'hidden',
  },
  photos: {
    type: 'custom',
    dependencies: ['id', 'photos'],
    component: FormInputImage,
    show: showPhotos,
    order: 50,
  },
  status: {
    type: 'readonly',
    field: 'status.name',
    order: 100,
  },
  sah_claim_id: {
    type: 'custom',
    caption: 'Support at Home Claim',
    component: FormInputClaimLink,
    show: (values: any) => !!values.sah_claim_id,
    order: 101,
  },
  service_branches: {
    type: 'select-lazy',
    field: 'branch_id',
    caption: 'Service Branch',
    show: showServiceBranches,
    selectorActions: getServiceBranches,
    selectorOptions: selectServiceBranches,
    colSize: 4,
    order: 180,
  },
  client_name: {
    type: 'readonly',
    field: 'client.full_name',
    caption: 'Client / Facility',
    deriveDescription: deriveClientAddress,
    className: 'text-left',
    deriveLink: deriveClientLink,
    required: true,
    colSize: 4,
    order: 200,
  },
  client_hold_check: {
    type: 'custom',
    component: FormInputClientHoldCheck,
    dependencies: ['date', 'client_id', 'repeat', 'repeat_start'],
    show: showClientHoldCheck,
    colSize: 4,
    order: 210,
  },
  package_fixed_id: {
    type: 'custom',
    component: FormInputPackageSelector,
    dependencies: ['package_fixed_id', 'client_id', 'date'],
    onChange: onChangePackageHandler(false),
    required: true,
    colSize: 6,
    order: 300,
  },
  client_description: {
    type: 'alert-info',
    className: 'form-notes',
    field: 'client.description',
    colSize: 4,
    show: showClientDescription,
    order: 400,
  },
  location_override_id: {
    type: 'select-lazy',
    caption: 'Location',
    colSize: 4,
    description: 'Optional, defaults to client home address',
    order: 500,
    selectorOptions: selectLocations,
    selectorActions: getLocations,
    selectorParams: { sort: ['name'], deleted: false },
    show: showLocations,
  },
  service_type_id: {
    type: 'select-lazy',
    caption: 'Service Type',
    selectorOptions: selectAppointmentServiceTypes,
    selectorActions: getServiceTypes,
    selectorParams: deriveServiceTypeSelectorParams,
    onChange: onChangeServiceType,
    deriveWarning: deriveServiceTypeDescription,
    validate: validatePackageServiceTypeCompatibility,
    initialiseInitialValue: initialise('service_type'),
    order: 600,
    colSize: 4,
    required: true,
    manualReload: true,
  },
  locations: {
    type: 'custom',
    caption: 'Trip Points',
    component: FormInputLocations,
    selectorOptions: selectLocations,
    selectorActions: getLocations,
    selectorParams: { sort: ['name'], deleted: false },
    dependencies: ['client_id', 'transport_pickup_id', 'transport_dropoff_id', 'transport_pickup', 'transport_dropoff'],
    order: 700,
    colSize: 6,
    show: showTransport,
  },
  accounts_definitions: {
    type: 'custom',
    component: FormInputNDISRates,
    deriveValue: deriveAccountsDefinition,
    dependencies: ['client_id', 'service_type_id', 'accounts_definitions'],
    order: 800,
  },
  qualification_level: {
    type: 'custom',
    caption: 'Qualifications Required',
    component: FormInputQualifications,
    dependencies: ['service_type_id', 'qualification_level'],
    validateConfirm: validateUserQualifications,
    selectorOptions: selectCareQualificationLevels,
    order: 900,
  },
  user_id: {
    type: 'custom',
    caption: 'Care Worker',
    component: FormInputCareWorkers,
    dependencies: ['client_id', 'user_id', 'date', 'id', 'repeat'],
    validateConfirm: validateCareWorker,
    initialiseInitialValue: initialiseUserOption,
    colSize: 6,
    order: 910,
  },
  user_leave_availability: {
    type: 'custom',
    component: FormInputWorkerLeaveAvailability,
    dependencies: ['date', 'qualification_level', 'user_id', 'repeat', 'repeat_start', 'repeat_weekday'],
    show: showUserAvailability,
    colSize: 4,
    order: 920,
  },
  user_appointments: {
    type: 'custom',
    component: FormInputWorkerAppointments,
    dependencies: [
      'date',
      'qualification_level',
      'user_id',
      'repeat',
      'repeat_start',
      'repeat_weekday',
      'repeat_next_date',
    ],
    show: showUserAvailability,
    colSize: 6,
    order: 930,
  },
  user_qualification_check: {
    type: 'custom',
    component: FormInputWorkerQualificationCheck,
    dependencies: ['date', 'qualification_level', 'user_id', 'repeat', 'repeat_start', 'repeat_weekday'],
    show: showUserAvailability,
    colSize: 4,
    order: 940,
  },
  user_rostering_notes: {
    type: 'custom',
    component: FormInputWorkerRosterNotes,
    dependencies: ['date', 'qualification_level', 'user_id', 'client_id', 'repeat', 'repeat_start', 'repeat_weekday'],
    show: showUserAvailability,
    colSize: 4,
    order: 950,
  },
  user_availability: {
    type: 'custom',
    component: FormInputWorkerAvailability,
    dependencies: ['date', 'qualification_level', 'user_id', 'repeat', 'repeat_start', 'repeat_weekday'],
    show: showUserAvailability,
    colSize: 6,
    order: 960,
  },
  start_end_time: {
    type: 'custom',
    caption: 'Start/End Times',
    colSize: 6,
    component: FormInputStartEndTimes,
    dependencies: ['date', 'start_time', 'end_time', 'ends_next_day', 'service_type_id'],
    validateConfirm: validateUserAvailability,
    validate: validateStartEndTimes,
    onChange: onChangeTime,
    order: 1000,
  },
  duration: {
    type: 'custom',
    caption: 'Duration',
    component: LabelDuration,
    deriveValue: deriveDuration,
    className: 'fw-bold',
    colSize: 4,
    order: 1100,
  },
  payment_type: {
    type: 'radio',
    field: 'payment_type',
    selectorOptions: selectPaymentTypes,
    show: showPaymentCollected,
    caption: 'Payment Type',
    colSize: 4,
    order: 1190,
  },
  payment_amount: {
    type: 'currency',
    field: 'payment_amount',
    caption: 'Payment Collected',
    show: showPaymentAmount,
    colSize: 4,
    order: 1200,
  },
  accounts: {
    type: 'custom',
    caption: 'Accounts',
    component: FormInputAccountSettings,
    deriveValue: deriveAccountSettings,
    colSize: 6,
    order: 1300,
  },
  flexibility: {
    type: 'switch',
    caption: 'Flexible with Time',
    order: 1400,
  },
  tasks: {
    type: 'custom',
    field: 'task_ids',
    caption: 'Tasks',
    labelCaption: 'Max 45',
    colSize: 6,
    component: FormInputTasks,
    selectorOptions: selectTaskTypes,
    validate: validateTasks,
    order: 1500,
  },
  medications: {
    type: 'multi-checkbox',
    field: 'medication_ids',
    caption: 'Medications',
    colSize: 3,
    selectorOptions: selectMedications,
    show: showMedications,
    order: 1600,
  },
  equipment_details: {
    type: 'custom',
    caption: 'Equipment Details',
    colSize: 6,
    component: FormInputEquipments,
    selectorOptions: selectEquipments,
    show: showEquipment,
    order: 1700,
  },
  notes: {
    type: 'textarea',
    caption: 'Notes',
    placeholder: 'Instructions for care workers, specific to this appointment',
    order: 1800,
  },
};

export { commonFields };
